import { useRef, useState } from 'react';
import styled from 'styled-components';
import { LauncherWindowData, useLauncherWindowStore } from '../../../../hooks/stores/launcherWindowStore';
import { SessionData, useSessionStore } from '../../../../hooks/stores/sessionStore';
import { UIData, useUIStore } from '../../../../hooks/stores/uiStore';
import { Dropdown } from '../../../common/Dropdown';
import { DropdownContentContainer } from '../../../common/DropdownContentContainer';
import { TextInput } from '../../../common/baseInputs/TextInput';

export const FontsPage = () => {
  const [sessionData] = useSessionStore<SessionData>();
  const [UIData, setUIData] = useUIStore<UIData>();
  const [launcherWindowData, setLauncherWindowData] = useLauncherWindowStore<LauncherWindowData>();
  const [isFontDropdownOpen, setIsFontDropdownOpen] = useState(false);
  const [fontNameToAdd, setFontNameToAdd] = useState('');
  const [errorText, setErrorText] = useState('');

  const inputRef = useRef<HTMLInputElement>(null);

  const onFontSubmit = () => {
    if (fontNameToAdd) {
      if (
        launcherWindowData.fonts.some((f) => f.fontFamily === fontNameToAdd)
      ) {
        inputRef.current?.focus();
        setFontNameToAdd('');
        setErrorText('Font is already added');
        return;
      }

      fetch(
        'https://fonts.googleapis.com/css2?family=' +
          fontNameToAdd.replace(/ /g, '+')
      )
        .then((res) => {
          if (res.status === 200) {
            setLauncherWindowData({
              ...launcherWindowData,
              fonts: [
                ...launcherWindowData.fonts,
                {
                  fontFamily: fontNameToAdd,
                  src:
                    'https://fonts.googleapis.com/css2?family=' +
                    fontNameToAdd.replace(/ /g, '+') +
                    ':wght@100;200;300;400;500;600;700;800;900',
                },
              ],
            });
            setIsFontDropdownOpen(false);
          }
          if (res.status === 400) {
            inputRef.current?.focus();
            setErrorText('Font name is not valid');
          }
        })
        .catch((err) => {
          inputRef.current?.focus();
          setErrorText('Font name is not valid');
        });
    }
  };

  const _removeFont = (fontFamily: string) => {
    setLauncherWindowData({
      ...launcherWindowData,
      fonts: launcherWindowData.fonts.filter(
        (f) => f.fontFamily !== fontFamily
      ),
    });
  };

  const removeFont = (fontFamily: string) => {
    setUIData({
      isDialogOpen: true,
      dialogData: {
        label: 'Are you sure you want to delete this font?',
        children: (
          <>
            <p style={{ marginTop: 0 }}>{fontFamily}</p>
            <span style={{ fontFamily: fontFamily }}>
              0123456789 <br /> Aa Bb Cc Dd Ee Ff Gg Hh Ii Jj Kk Ll Mm Nn Oo Pp
              Qq Rr Ss Tt Uu Vv Ww Xx Yy Zz
            </span>
          </>
        ),
        options: [
          {
            text: 'Cancel',
            onClick: () => {
              setUIData({
                isDialogOpen: false,
              });
            },
          },
          {
            text: 'Delete',
            onClick: () => {
              _removeFont(fontFamily);
              setUIData({
                isDialogOpen: false,
              });
            },
            type: 'danger',
          },
        ],
        onClose: () =>
          setUIData({
            isDialogOpen: false,
          }),
      },
    });
  };

  return (
    <>
      {sessionData.themeID && (
        <Dropdown
          trigger={<AddButton>Add Font</AddButton>}
          isOpen={isFontDropdownOpen}
          setOpen={(isOpen) => {
            if (isOpen) {
              setErrorText('');
              setFontNameToAdd('');
              inputRef.current?.focus();
            }

            setIsFontDropdownOpen(isOpen);
          }}
          horizontalAlign="center"
          contentStyle={{ width: '100%' }}
          closeOnClick={false}
        >
          <DropdownContentContainer style={{ margin: '10px', gap: '5px' }}>
            <PopupHeader
              onClick={() => window.open('https://fonts.google.com/')}
            >
              <span>Google Fonts</span>
              <i className="icon-external" />
            </PopupHeader>
            <TextInput
              inputRef={inputRef}
              label="Font Name"
              value={fontNameToAdd}
              onChange={(value) =>
                setFontNameToAdd(value?.replace(/\+/g, ' ') || '')
              }
              onBlur={onFontSubmit}
            />

            <span style={{ color: 'var(--error-1)', padding: '0 5px' }}>
              {errorText}
            </span>

            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <SubmitButton onClick={onFontSubmit}>Add</SubmitButton>
            </div>
          </DropdownContentContainer>
        </Dropdown>
      )}

      <ScrollContainer className="scroll">
        <Grid>
          {launcherWindowData.fonts?.map((font, i) => (
            <Item key={font.fontFamily + '-' + i}>
              <span style={{ fontFamily: font.fontFamily }}>
                {font.fontFamily}
              </span>
              <span className="preview" style={{ fontFamily: font.fontFamily }}>
                0123456789 <br /> Aa Bb Cc Dd Ee Ff Gg Hh Ii Jj Kk Ll Mm Nn Oo
                Pp Qq Rr Ss Tt Uu Vv Ww Xx Yy Zz
              </span>

              {sessionData.themeID && (
                <div
                  className="removeButton"
                  onClick={() => removeFont(font.fontFamily)}
                >
                  <i className="icon-x" />
                </div>
              )}
            </Item>
          ))}
        </Grid>
      </ScrollContainer>
    </>
  );
};

const ScrollContainer = styled.div`
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
`;

const AddButton = styled.div`
  cursor: pointer;
  padding: var(--padding-1);
  margin: var(--padding-1);
  margin-bottom: 0;
  display: flex;
  justify-content: center;
  width: 100%;

  border: 1px dashed var(--neutral-3);
  border-radius: var(--radius-1);

  color: var(--main-2);

  transition:
    color 0.3s ease,
    border 0.3s ease;
  &:hover {
    color: var(--main-1);
    border: 1px dashed var(--main-1);
  }
`;

const PopupHeader = styled.div`
  cursor: pointer;
  padding: var(--padding-1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 5px;

  background-color: var(--panel-bg-color);
  border-bottom: 1px solid var(--neutral-3);

  transition: color 0.3s ease;
  &:hover {
    color: var(--main-1);
  }
`;

const SubmitButton = styled.div`
  box-sizing: border-box;
  cursor: pointer;

  width: 100px;
  padding: 5px;
  display: flex;
  justify-content: center;

  border: 1px solid var(--neutral-3);
  border-radius: var(--radius-1);

  color: var(--main-2);

  transition:
    color 0.3s ease,
    border 0.3s ease;
  &:hover {
    color: var(--main-1);
    border: 1px solid var(--main-1);
  }
`;

const Grid = styled.div`
  box-sizing: border-box;
  width: 100%;
  padding: var(--padding-1);
  display: flex;
  flex-wrap: wrap;
  gap: var(--padding-1);

  animation: slideLeft 1s cubic-bezier(0.075, 0.82, 0.165, 1);
`;

const Item = styled.div`
  position: relative;
  flex: 25%;
  max-width: 250px;
  min-width: 100px;
  aspect-ratio: 1;

  outline: 1px solid transparent;
  border-radius: var(--radius-2);
  overflow: hidden;

  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;

  & > span {
    pointer-events: none;
    box-sizing: border-box;
    width: 100%;
    padding: 2px 5px;

    color: #eee;
    text-align: center;
    font-size: 24px;

    overflow: hidden;
    text-overflow: ellipsis;
  }

  transition: outline-color 0.3s ease;

  &:hover {
    outline-color: var(--neutral-3);

    & > .preview {
      opacity: 1;
    }
  }

  &:hover > .removeButton {
    opacity: 1;
  }
  .removeButton {
    transition:
      color 0.3s ease,
      opacity 0.3s ease;

    opacity: 0;
    background-color: var(--panel-bg-color);
    border-radius: 5px;

    position: absolute;
    top: 0;
    right: 0;
    padding: 5px;
    margin: 5px;

    &:hover {
      background-color: var(--neutral-1);
    }
  }

  .preview {
    box-sizing: border-box;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;

    display: flex;
    justify-content: center;
    align-items: center;

    font-size: 12px;
    background-color: var(--neutral-2);

    opacity: 0;
    transition: opacity 0.3s ease;
  }
`;
